import React, { useState } from 'react';
import RTModal from '../common/RTModal';
import RTButton from '../common/RTButton';
import apiClient from '../../api/apiClient';
import { toastSuccess, toastError } from '../common/toast';
import './ComplaintManagement.css';

const UpdateComplaintStatusModal = ({ 
  isOpen, 
  onClose, 
  complaint, 
  action, // 'start', 'resolve', 'close', or 'addNote'
  onSuccess 
}) => {
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(false);

  // Helper function to format status for display
  const formatStatus = (status) => {
    const statusMap = {
      'open': 'Open',
      'assigned': 'Assigned',
      'in_progress': 'In Progress',
      'resolved': 'Resolved',
      'closed': 'Closed'
    };
    return statusMap[status] || status;
  };

  // Configuration for different actions
  const actionConfigs = {
    start: {
      title: 'Start Ticket',
      buttonText: 'Start Ticket',
      placeholder: 'Add any notes about starting work on this ticket...',
      isNoteRequired: false,
      defaultNote: 'Ticket started - work in progress',
      newStatus: 'in_progress'
    },
    resolve: {
      title: 'Resolve Ticket',
      buttonText: 'Resolve Ticket',
      placeholder: 'Add resolution details or notes...',
      isNoteRequired: false,
      defaultNote: 'Ticket marked as resolved',
      newStatus: 'resolved'
    },
    close: {
      title: 'Close Complaint',
      buttonText: 'Close Complaint',
      placeholder: 'Add any final comments about the complaint resolution...',
      isNoteRequired: false,
      defaultNote: 'Complaint closed',
      newStatus: 'closed'
    },
    addNote: {
      title: 'Add Note',
      buttonText: 'Add Note',
      placeholder: 'Add your note here...',
      isNoteRequired: true,
      defaultNote: '',
      newStatus: null // No status change for adding notes
    }
  };

  const config = actionConfigs[action] || actionConfigs.addNote;

  const handleConfirm = async () => {
    const noteToSend = note.trim() || config.defaultNote;
    
    if (config.isNoteRequired && !note.trim()) {
      toastError('Please enter a note');
      return;
    }

    try {
      setLoading(true);
      
      const payload = {};
      if (config.newStatus) {
        payload.status = config.newStatus;
      }
      if (noteToSend) {
        payload.notes = noteToSend;
        payload.timestamp = new Date().toLocaleString('sv-SE'); // ISO-like format in local time: YYYY-MM-DD HH:mm:ss
      }

      await apiClient.post(`/complaints/${complaint.complaint_id}/updateComplaint`, payload);

      toastSuccess(`${config.title} completed successfully!`);
      onSuccess();
      onClose();
      setNote(''); // Clear the note field
    } catch (error) {
      console.error(`Error with ${action}:`, error);
      toastError(error.message || `Failed to ${action.replace(/([A-Z])/g, ' $1').toLowerCase()}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    handleConfirm();
  };

  const handleCancel = () => {
    setNote('');
    onClose();
  };

  return (
    <RTModal
      isOpen={isOpen}
      onClose={handleCancel}
      title={config.title}
      size="medium"
    >
      <div className="update-complaint-modal">
        <div className="complaint-summary">
          <h5 style={{ color: '#153C7F' }}>Complaint #{complaint?.complaint_id}</h5>
          <p><strong style={{ color: '#153C7F' }}>Type:</strong> {complaint?.complaint_type}</p>
          <p><strong style={{ color: '#153C7F' }}>Description:</strong> {complaint?.description}</p>
          <p><strong style={{ color: '#153C7F' }}>Current Status:</strong> {formatStatus(complaint?.status)}</p>
          {config.newStatus && (
            <p><strong style={{ color: '#153C7F' }}>New Status:</strong> {formatStatus(config.newStatus)}</p>
          )}
        </div>


        <form onSubmit={handleSubmit}>
          <div className="form-group mb-3">
            <label style={{ color: '#153C7F', fontWeight: '500' }}>
              Notes {config.isNoteRequired && <span style={{ color: '#dc3545' }}>*</span>}
              {!config.isNoteRequired && <span style={{ color: '#6c757d', fontWeight: 'normal' }}> (Optional)</span>}
            </label>
            <textarea
              className="form-control"
              rows="4"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={config.placeholder}
              required={config.isNoteRequired}
            />
          </div>

          <div className="modal-actions" style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            gap: '15px',
            marginTop: '20px' 
          }}>
            <RTButton
              variant="outline"
              onClick={handleCancel}
              disabled={loading}
            >
              Cancel
            </RTButton>
            <RTButton
              variant='primary'
              onClick={handleConfirm}
              loading={loading}
            >
              {config.buttonText}
            </RTButton>
          </div>
        </form>
      </div>
    </RTModal>
  );
};

export default UpdateComplaintStatusModal;
